import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminSpinner } from '../../adminComponents/AdminSpinner/adminSpinner';
import { cancelViktorRun } from '../../api/conversations.api';
import { CloudUploadIcon } from '../../assets/svg';
import { reportError } from '../../helpers/reportError';
import { useElementHeight } from '../../hooks/useElementHeight';
import { useViktorRunState } from '../../store/viktorRun';
import { Div } from '../../styles/styles';
import {
  ConversationMessageAudience,
  ConversationMessageEntity,
  ConversationMessageWithStatus,
  ConversationType,
} from '../../types/conversation.types';
import {
  ChatScrollArea,
  ChatThreadWrapper,
  ComposerClearance,
  ComposerSlot,
  DropOverlay,
  JumpToLatestButton,
  ThreadBody,
  ThreadHeader,
  ThreadHeaderSlot,
} from './ChatThread.styled';
import { ComposerWrapper } from './chatSurfaces.styled';
import {
  ACCEPTED_MIMES,
  MAX_ATTACHMENTS_PER_MESSAGE,
  validateAttachment,
} from './chatUpload';
import {
  ChatInput,
  ChatInputSubmit,
  MentionCandidate,
} from './components/ChatInput';
import { ImageEditor } from './components/ImageEditor';
import { MessageActionSheet } from './components/MessageActionSheet';
import { isEditableImage } from './components/imageEditorCore';
import { AssistantThinkingBubble } from './components/AssistantThinkingBubble';
import { AudienceOption, AudienceSwitch } from './components/AudienceSwitch';
import { MessageList } from './components/MessageList';
import {
  HiddenFileInput,
  StagedAttachments,
} from './components/StagedAttachments';
import { VoiceRecorderBar } from './components/VoiceRecorderBar';
import { ChatEmptyState } from './components/ChatEmptyState';
import { canMentionAll } from './helpers/chatMentions';
import { canTranslate, ChatViewer, OpenUser } from './helpers/chatMessage';
import { assistantThinkingLabel } from './helpers/chatTriage';
import { useChatAttachments } from './hooks/useChatAttachments';
import { useChatMessages } from './hooks/useChatMessages';
import { useChatRead } from './hooks/useChatRead';
import { useFileDropzone } from './hooks/useFileDropzone';
import { useMessageFocus } from './hooks/useMessageFocus';
import { useMessageMutations } from './hooks/useMessageMutations';
import { useMessageTranslations } from './hooks/useMessageTranslations';
import { useSendChatMessage } from './hooks/useSendChatMessage';
import { useSendImage } from './hooks/useSendImage';
import { useThreadAutoScroll } from './hooks/useThreadAutoScroll';
import { useThreadPresence } from './hooks/useThreadPresence';
import {
  supportedRecordingMime,
  useVoiceRecorder,
} from './hooks/useVoiceRecorder';

type Props = {
  /**
   * On admin this is the real conversation id. On the merchant side the routes
   * carry no id at all, so it is the `USER_CONVERSATION_ID` sentinel and only
   * ever used as a cache key.
   */
  conversationId: number | string | null;
  /**
   * Decides what "mine" means. In `support` the vGrubs side is a collective and
   * alignment follows the side; in `direct` and `group` everyone is internal,
   * so it follows the sender id. `viktor` is text only: no attachments, no
   * reply, no edit or delete, and Send becomes Stop while Viktor is writing.
   * Defaults to `support`, which is the only kind the merchant app has.
   */
  conversationType?: ConversationType;
  /** The logged in user, for authorship and "You" in system messages. */
  viewerId?: number | null;
  /** False for a viewer with read but not write access. */
  canWrite?: boolean;
  /**
   * How many messages here the viewer has not read, as the opening surface
   * knows it - the inbox row's count on admin, the badge on the merchant.
   * With it a thread opens on the "New messages" divider and reading starts
   * where it stopped; without it, or at 0, it opens at the newest message.
   */
  unreadCount?: number;
  /** Sender shown on optimistic bubbles before the server responds. */
  currentSender?: ConversationMessageEntity['sender'];
  /** Name used when a message carries no sender of its own. */
  fallbackSenderName?: string | null;
  /** Set when the thread fills a parent that already bounds its height. */
  embedded?: boolean;
  /** Set when the parent already draws the card, so this one draws none. */
  bare?: boolean;
  /**
   * Title bar rendered inside the card, above the messages. Lives here rather
   * than above the component so the name, the conversation and the composer
   * share one surface.
   */
  header?: React.ReactNode;
  onOpenOrder?: (orderId: number) => void;
  /**
   * Opens a support ticket the AI triage bot raised, in the support tickets
   * list. Admin only: the merchant acts on the card itself and has no such
   * screen to be sent to.
   */
  onOpenTicket?: (ticketId: number) => void;
  /**
   * Opens a sender's detail screen from the name under their bubble. Left off
   * on the merchant app, which has no such screen - and where every vGrubs
   * sender is anonymised anyway.
   */
  onOpenUser?: OpenUser;
  /**
   * The line shown when the thread has no messages. A string, not a node:
   * every caller wants the same quiet line in the same place, and passing
   * markup is how three surfaces ended up with three of them.
   */
  emptyState?: string;
  /**
   * A heading over that line, for the surfaces where the empty thread is an
   * invitation rather than a state: the merchant's one support thread opens on
   * it and is meant to be started. An admin's is one of many, so it stays a
   * single line.
   */
  emptyStateTitle?: string;
  /**
   * False hides the attach and voice affordances and disables the dropzone -
   * for transports with no attachment backing. The default keeps every
   * existing surface unchanged.
   */
  allowAttachments?: boolean;
  /**
   * False hides reply, edit and delete on every bubble - for a backend with
   * no such routes (the vDrive driver chat). Copy stays. The default keeps
   * every existing surface unchanged.
   */
  allowMessageActions?: boolean;
  /**
   * Everyone in the conversation, the reader included. Group and direct only -
   * a support thread has nobody to mention.
   *
   * Two jobs, which is why the reader is in it: the composer's `@` menu offers
   * the others, and every bubble uses the full list to tell a name written in
   * a body from the words around it. Leaving yourself out would highlight
   * everyone's name except the one that matters most - your own, in the
   * message that was addressed to you.
   */
  members?: MentionCandidate[];
  /**
   * Who the composer may write to, first one picked - a dispatcher thread
   * offers the customer, the fleet and an internal note. Absent everywhere
   * else, where every message goes to everyone in the thread.
   */
  audiences?: AudienceOption[];
};

/**
 * One conversation, used by both apps and by all three conversation types.
 *
 * Which messages sit on the right is decided by `isOwnMessage`, not by the
 * bundle: a support thread has a collective vGrubs side, while a direct or a
 * group has internal users on both sides and can only be told apart by sender
 * id. See `helpers/chatMessage` for the three rules.
 *
 * Everything with a life of its own is a hook beside this file - paging and
 * scroll anchoring, where the reader is put, the socket room, attachments, the
 * recorder, the reply jump. What is left here is the wiring between them.
 */
export const ChatThread = ({
  conversationId,
  conversationType = 'support',
  viewerId = null,
  canWrite = true,
  unreadCount = 0,
  currentSender = null,
  fallbackSenderName,
  embedded,
  bare,
  header,
  onOpenOrder,
  onOpenTicket,
  onOpenUser,
  emptyState,
  emptyStateTitle,
  allowAttachments = true,
  allowMessageActions = true,
  members,
  audiences,
}: Props) => {
  const [replyTo, setReplyTo] = useState<ConversationMessageWithStatus | null>(
    null
  );
  const [editing, setEditing] = useState<ConversationMessageWithStatus | null>(
    null
  );
  /**
   * The message a press-and-hold opened the action sheet for.
   *
   * Held here rather than per bubble so one sheet exists for the whole thread -
   * a mounted overlay per message would be hundreds of them in a long
   * conversation.
   */
  const [heldMessage, setHeldMessage] = useState<{
    message: ConversationMessageWithStatus;
    canModify: boolean;
  } | null>(null);

  const {
    messages,
    scrollRef,
    topSentinelRef,
    scrollToBottom,
    scrollToUnreadMarker,
    isPinnedToBottom,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useChatMessages(conversationId);

  /*
   * A Viktor thread is a conversation with a machine, and most of a human
   * thread's furniture is a lie there: nothing to attach, nobody to mention or
   * reply to, and an edit or a delete changes a stored question without ever
   * reaching Viktor again. Everything gated on this below is simply absent
   * rather than disabled - the honest offer is a text box.
   */
  const viktorThread = conversationType === 'viktor';
  /* Viktor takes text only and his answers are not ours to edit; a host can
     also switch these off (the vDrive console's driver threads). */
  const canAttach = allowAttachments && !viktorThread;
  const messageActions = allowMessageActions && !viktorThread;
  /* Translate is in every thread, Viktor's included - but not where a host
     switched the message actions off: the vDrive console has no translate
     route of its own. */
  const translationsOn = allowMessageActions;
  /** Only a group has an `@all`, in the composer and in its bubbles. */
  const groupThread = canMentionAll(conversationType);

  const { markRead } = useChatRead(conversationId);
  /* Observed like any other thread - the socket needs to know it is on screen
     so an `inbox_updated` for it does not reset the history under a streaming
     answer - but Viktor's room needs no joining, which the socket handles. */
  const { onTyping, typingUsers, assistantState } =
    useThreadPresence(conversationId);

  /*
   * What the support bot is doing, in words - and only its own thread's.
   * Viktor's phase is not read here: it belongs to the bubble he is writing
   * into, which draws it itself.
   */
  const assistantThinking =
    !viktorThread && assistantState
      ? assistantThinkingLabel(assistantState)
      : null;

  /**
   * The answer Viktor is writing in THIS thread, if any. The store holds one
   * run at a time; it is compared against the open thread rather than trusted,
   * so a run can never put Stop on somebody else's composer.
   */
  const viktorRun = useViktorRunState((state) => state.run);
  const viktorWriting =
    viktorThread &&
    viktorRun !== null &&
    viktorRun.conversationId === Number(conversationId);

  const stopViktor = useCallback(async () => {
    if (!viktorRun || conversationId === null) return;
    const { runId } = viktorRun;
    /* Held until `viktor_run_finished` says cancelled, ~2s later - the button
       greys rather than the bubble vanishing early, since a cancelled answer
       is dropped by the socket and a failed cancel keeps it. */
    useViktorRunState.getState().setCancelling(runId, true);
    try {
      await cancelViktorRun(conversationId);
    } catch (error) {
      useViktorRunState.getState().setCancelling(runId, false);
      reportError(error);
    }
  }, [viktorRun, conversationId]);

  /** Everything the bubbles need to decide whose message is whose. */
  const viewer: ChatViewer = useMemo(
    () => ({ id: viewerId, type: conversationType }),
    [viewerId, conversationType]
  );

  const { hasNewBelow, firstUnreadId, onScroll, jumpToLatest } =
    useThreadAutoScroll({
      conversationId,
      isLoading,
      messages,
      viewer,
      unreadCount,
      hasNextPage: hasNextPage ?? false,
      isFetchingNextPage,
      fetchNextPage,
      scrollToBottom,
      scrollToUnreadMarker,
      isPinnedToBottom,
      markRead,
      onOpen: () => {
        setReplyTo(null);
        setEditing(null);
      },
    });

  /*
   * Follow the bot's bubble down, on the same terms as a message: only for a
   * reader already at the newest one. It appears below everything else and is
   * not in `messages`, so the thread's own pinning never sees it arrive.
   */
  const thinkingShown = !!assistantThinking;
  useEffect(() => {
    /* On the flag, not on the words: the phase changes several times inside
       one wait, and only its arrival moves anything. */
    if (thinkingShown && isPinnedToBottom()) scrollToBottom();
  }, [thinkingShown, isPinnedToBottom, scrollToBottom]);

  const { send, retry } = useSendChatMessage({
    conversationId: conversationId ?? 0,
    sender: currentSender,
    onSent: () => scrollToBottom('smooth'),
  });

  const { editMessage, deleteMessage } = useMessageMutations(conversationId);
  const translations = useMessageTranslations(conversationId);
  const { sendImage } = useSendImage({ conversationId, send });

  const attachments = useChatAttachments(conversationId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /** The image sitting in the draft/edit screen before it is sent. */
  const [editorImage, setEditorImage] = useState<File | null>(null);

  const { addFiles } = attachments;
  /**
   * One editable image goes through the draft screen - crop, draw, caption -
   * the way Telegram stages a photo. Everything else keeps the staged flow:
   * several files at once, pdfs, video, and the images the editor cannot
   * decode or would flatten (heic, gif).
   */
  const stageOrEditFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      if (
        list.length === 1 &&
        isEditableImage(list[0]) &&
        !validateAttachment(list[0])
      ) {
        setEditorImage(list[0]);
        return;
      }
      addFiles(list);
    },
    [addFiles]
  );

  /* Who the next message goes to. Back to the first choice - the customer -
     in every thread opened, so a note never leaks out of the last one. */
  const [audience, setAudience] = useState<ConversationMessageAudience>(
    'everyone'
  );
  const audienceOption = audiences?.find((option) => option.value === audience);

  // A draft, and an open action sheet, belong to the thread they came from.
  useEffect(() => {
    setEditorImage(null);
    setHeldMessage(null);
    setAudience(audiences?.[0]?.value ?? 'everyone');
    // Only on a new thread - the options are rebuilt every render.
  }, [conversationId]);

  /*
   * The header and the composer both float over the conversation, so their real
   * heights have to be published for the scroll area's padding and the jump
   * button. Measured rather than assumed: a reply strip, a row of staged files
   * or a multi-line draft each change the composer's.
   */
  const headerBox = useElementHeight<HTMLDivElement>(!!header);
  const composerBox = useElementHeight<HTMLDivElement>();

  const recorder = useVoiceRecorder((blob, mime) =>
    attachments.addRecording(blob, mime)
  );

  // Hidden when the browser can't produce any container the backend accepts,
  // rather than letting someone record something that will be rejected.
  const canRecord = useMemo(() => supportedRecordingMime() !== null, []);

  /**
   * Drop files anywhere on the thread to stage them, as Telegram and Discord
   * do. Disabled while editing, mirroring the composer's own rule: an edit only
   * changes the body, so a file staged there would upload and then be silently
   * dropped on save. Also off while recording, where the staged row is unmounted.
   */
  const { isDraggingOver, dropzoneProps } = useFileDropzone({
    /* Also off in a Viktor thread, which is text only - with the attach and
       mic buttons absent below, this seals the last way a file could get in
       (the API answers 400 to one). */
    enabled:
      canAttach &&
      canWrite &&
      conversationId !== null &&
      !editing &&
      !recorder.isRecording,
    onFiles: stageOrEditFiles,
  });

  const { highlightMessageId, focusMessage } = useMessageFocus({
    hasNextPage,
    fetchNextPage,
  });

  const onEdit = useCallback((message: ConversationMessageWithStatus) => {
    setReplyTo(null);
    setEditing(message);
  }, []);

  const onReply = useCallback((message: ConversationMessageWithStatus) => {
    setEditing(null);
    setReplyTo(message);
  }, []);

  const submitEdit = async (body: string) => {
    if (!editing) return;
    // Stays in edit mode when the save fails, so the draft is not lost.
    if (await editMessage(editing.id, body)) setEditing(null);
  };

  const onSubmit = (payload: ChatInputSubmit) => {
    send({
      body: payload.body,
      replyToId: payload.replyToId,
      mentionUserIds: payload.mentionUserIds,
      mentionAll: payload.mentionAll,
      attachmentIds: attachments.attachmentIds,
      ...(audiences ? { audience } : {}),
    });
    setReplyTo(null);
    attachments.clear();
  };

  if (conversationId === null) {
    return (
      <ChatThreadWrapper $embedded={embedded} $bare={bare}>
        <Div $flex $center $height="100%" $p="24px">
          <ChatEmptyState text="Select a conversation to get started." />
        </Div>
      </ChatThreadWrapper>
    );
  }

  return (
    <ChatThreadWrapper
      $embedded={embedded}
      $bare={bare}
      style={
        {
          ...(composerBox.height === null
            ? null
            : { '--chat-composer-height': `${composerBox.height}px` }),
          ...(headerBox.height === null
            ? null
            : { '--chat-header-height': `${headerBox.height}px` }),
        } as React.CSSProperties
      }
      {...dropzoneProps}
    >
      {/* Always mounted so the fade has a starting state to animate from. */}
      <DropOverlay $visible={isDraggingOver} aria-hidden={!isDraggingOver}>
        <CloudUploadIcon />
        <span>Drop files to attach</span>
        <small>
          Images, video, audio or PDF - up to {MAX_ATTACHMENTS_PER_MESSAGE}{' '}
          files
        </small>
      </DropOverlay>

      {/* The data attribute lets scrollToUnreadMarker measure the header
          straight off the DOM. The CSS variable is one commit behind on a
          fresh mount - the phone's inbox->thread flow - and a divider placed
          against the stale value opens the thread with its marker hidden
          under this very bar. */}
      {header && (
        <ThreadHeaderSlot ref={headerBox.ref} data-chat-header>
          <ThreadHeader>{header}</ThreadHeader>
        </ThreadHeaderSlot>
      )}

      <ThreadBody>
        <ChatScrollArea ref={scrollRef} onScroll={onScroll}>
          {/* Sentinel is at the TOP: "next page" means older history, so
              scrolling up is what loads the next page here. Deliberately a bare
              1px marker with no spinner of its own - the loading indicator
              lives beside the topmost day pill instead, where it cannot land on
              top of it. */}
          <div ref={topSentinelRef} style={{ height: 1, flex: 'none' }} />

          {/* Centred on the conversation, not on the card: ThreadBody is the
              positioned ancestor, so it lands between the header and the
              composer rather than on top of either. */}
          {isLoading && <AdminSpinner absolute small $noBackground />}

          {!isLoading && messages.length === 0 && (
            <Div $flex $center $height="100%" $heightSM="100%">
              <ChatEmptyState
                title={emptyStateTitle}
                text={emptyState ?? 'No messages yet. Start the conversation.'}
              />
            </Div>
          )}

          <MessageList
            messages={messages}
            viewer={viewer}
            fallbackSenderName={fallbackSenderName}
            canWrite={canWrite}
            firstUnreadId={firstUnreadId}
            /* Absent, not disabled, in a Viktor thread - the gutter and the
               hold sheet hide what they are not handed, leaving Copy. */
            onReply={messageActions ? onReply : undefined}
            onEdit={messageActions ? onEdit : undefined}
            onDelete={messageActions ? deleteMessage : undefined}
            onRetry={(message) =>
              retry(message, { body: message.body ?? undefined })
            }
            onLongPress={(message, canModify) =>
              setHeldMessage({ message, canModify })
            }
            onOpenOrder={onOpenOrder}
            onOpenTicket={onOpenTicket}
            onOpenReply={focusMessage}
            onOpenUser={onOpenUser}
            members={members}
            mentionAll={groupThread}
            translationOf={translations.stateOf}
            onTranslate={translationsOn ? translations.toggle : undefined}
            highlightMessageId={highlightMessageId}
            loadingOlder={isFetchingNextPage}
          />

          {/* After the list rather than in it: it is not a message, it has no
              id and nothing may reply to it - but it stands in the row a
              message would, so the answer replacing it moves nothing. */}
          {assistantThinking && (
            <AssistantThinkingBubble label={assistantThinking} />
          )}

          {/* Only with something to clear: the empty state above already fills
              the height, and a spacer under it would make it scroll. */}
          {messages.length > 0 && <ComposerClearance />}
        </ChatScrollArea>

        {hasNewBelow && (
          <JumpToLatestButton type="button" onClick={jumpToLatest}>
            New messages
          </JumpToLatestButton>
        )}
      </ThreadBody>

      <ComposerSlot ref={composerBox.ref}>
        {canWrite && recorder.isRecording ? (
          <ComposerWrapper>
            <VoiceRecorderBar
              recording={recorder.isRecording}
              seconds={recorder.seconds}
              onStart={recorder.start}
              onStop={recorder.stop}
              onCancel={recorder.cancel}
            />
          </ComposerWrapper>
        ) : (
          <ChatInput
            disabled={!canWrite}
            onSubmit={onSubmit}
            editing={editing}
            onCancelEdit={() => setEditing(null)}
            onSubmitEdit={submitEdit}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            /* Absent, not disabled, in a Viktor thread - the buttons hide when
               the handler is missing, and text only is the honest offer. */
            onPickFiles={
              canAttach ? () => fileInputRef.current?.click() : undefined
            }
            onStartRecording={
              canAttach && canRecord ? recorder.start : undefined
            }
            busy={attachments.uploading}
            hasAttachments={attachments.attachmentIds.length > 0}
            blockedReason={
              attachments.hasFailed
                ? 'An attachment failed to upload. Remove it or try again before sending.'
                : undefined
            }
            /* Offering yourself would be a mention you cannot receive - and
               `resolveMentions` would then put your own id on the payload. */
            mentionCandidates={members?.filter(
              (member) => member.id !== viewerId
            )}
            mentionAll={groupThread}
            typingNames={typingUsers
              .filter((typist) => typist.userId !== viewerId)
              .map((typist) => typist.name)}
            onStop={viktorWriting ? stopViktor : undefined}
            stopping={viktorWriting && viktorRun.cancelling}
            /* Nobody is listening for keystrokes in a Viktor thread. In a
               dispatcher thread the audience goes along, so a note being
               typed stays private. */
            onTyping={
              viktorThread
                ? undefined
                : audiences
                ? (typing: boolean) => onTyping(typing, audience)
                : onTyping
            }
            autoFocusKey={conversationId}
            placeholder={audienceOption?.placeholder}
          >
            {audiences && !editing && (
              <AudienceSwitch
                options={audiences}
                value={audience}
                onChange={setAudience}
              />
            )}
            <StagedAttachments
              staged={attachments.staged}
              onRemove={attachments.remove}
            />
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_MIMES}
              onChange={(event) => {
                if (event.target.files) stageOrEditFiles(event.target.files);
                // Reset so picking the same file twice still fires onChange.
                event.target.value = '';
              }}
            />
          </ChatInput>
        )}
      </ComposerSlot>

      {/* What a touch screen gets instead of the hover gutter. */}
      <MessageActionSheet
        target={heldMessage}
        onClose={() => setHeldMessage(null)}
        /* The sheet also opens for a reader who may not write, to translate -
           so Reply is gated here, where the bubble gates its gutter's. */
        onReply={messageActions && canWrite ? onReply : undefined}
        onEdit={messageActions ? onEdit : undefined}
        onDelete={messageActions ? deleteMessage : undefined}
        translation={heldMessage && translations.stateOf(heldMessage.message)}
        onTranslate={
          translationsOn &&
          heldMessage &&
          canTranslate(heldMessage.message, viewer)
            ? translations.toggle
            : undefined
        }
      />

      {/* Renders through the modal portal; its send carries an active reply
          along, exactly as the composer would have. */}
      {editorImage && (
        <ImageEditor
          file={editorImage}
          onClose={() => setEditorImage(null)}
          onSend={async (image, caption) => {
            const sent = await sendImage(image, caption, replyTo?.id);
            if (sent) {
              setEditorImage(null);
              setReplyTo(null);
            }
            return sent;
          }}
        />
      )}
    </ChatThreadWrapper>
  );
};
